import { CommonModule } from '@angular/common';
import { LocalizationService, LocalizationModule, SubscriptionService } from '@abp/ng.core';
import { Component, Injector, OnInit } from '@angular/core';
import {
  DwGridDataSourceService,
  FieldTypes,
  IEnumerationCellRendererParams,
} from '@digiwall/ag-grid';
import { ColDef } from '@ag-grid-community/core';
import {
  CustomerPreparationListDto,
  CustomerPreparationService,
  CustomerPreparationStatus,
} from '@digiwall/goliath-documents/proxy';
import { ActivatedRoute, Router } from '@angular/router';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { BaseCustomerDocList } from '../../base-customer-docs/base-customer-doc-list';
import { ListPageComponent } from '@digiwall/ag-grid';
import { AgGridComponent } from '@digiwall/ag-grid';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'document-customer-preparation-list',
  templateUrl: './customer-preparation-list.component.html',
  styles: `
    :host {
      flex-grow: 1;
    }
  `,
  providers: [DwGridDataSourceService, SubscriptionService],
  imports: [CommonModule, ListPageComponent, AgGridComponent, LocalizationModule],
})
export class CustomerPreparationListComponent
  extends BaseCustomerDocList<CustomerPreparationListDto, CustomerPreparationService>
  implements OnInit
{
  private _columns: ColDef[] = [
    ...this.colDefs,
    {
      field: 'preparationStatus',
      headerName: this.localizationService.instant('Doc::CustomerPreparation:Status'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.documentEnumerationTypeStore.customerPreparationStatus,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'lineCount',
      headerName: this.localizationService.instant('Doc::CustomerPreparation:LineCount'),
      type: FieldTypes.Number,
    },
  ];

  get columns(): ColDef[] {
    return this._columns;
  }

  get canStart(): boolean {
    const s = this.selectedEntities();
    return s.length > 0 && s.every(e => e.preparationStatus === CustomerPreparationStatus.ToDo);
  }

  get canComplete(): boolean {
    const s = this.selectedEntities();
    return (
      s.length > 0 && s.every(e => e.preparationStatus === CustomerPreparationStatus.InProgress)
    );
  }

  get canCancel(): boolean {
    const s = this.selectedEntities();
    return (
      s.length > 0 &&
      s.every(
        e =>
          e.preparationStatus === CustomerPreparationStatus.ToDo ||
          e.preparationStatus === CustomerPreparationStatus.InProgress,
      )
    );
  }

  constructor(
    customerPreparationService: CustomerPreparationService,
    router: Router,
    route: ActivatedRoute,
    localizationService: LocalizationService,
    subscriptionService: SubscriptionService,
    confirmation: ConfirmationService,
    datasource: DwGridDataSourceService,
    injector: Injector,
  ) {
    super(
      customerPreparationService,
      router,
      route,
      localizationService,
      subscriptionService,
      confirmation,
      datasource,
      injector,
    );

    const customerName = this.colDefs.find(c => c.field === 'thirdPartyName');
    if (customerName) {
      customerName.hide = false;
    }
  }

  protected bulkStart(): void {
    const ids = this.selectedEntities().map(e => e.id);
    forkJoin(ids.map(id => this.docService.startPreparation(id))).subscribe(() =>
      this.datasource.get(),
    );
  }

  protected bulkComplete(): void {
    this.confirmation
      .warn('Doc::CustomerPreparation:BulkCompleteConfirmation', '::AreYouSure')
      .subscribe(status => {
        if (status !== Confirmation.Status.confirm) return;
        const ids = this.selectedEntities().map(e => e.id);
        forkJoin(ids.map(id => this.docService.completePreparation(id))).subscribe(() =>
          this.datasource.get(),
        );
      });
  }

  protected bulkCancel(): void {
    this.confirmation
      .warn('Doc::CustomerPreparation:BulkCancelConfirmation', '::AreYouSure')
      .subscribe(status => {
        if (status !== Confirmation.Status.confirm) return;
        const ids = this.selectedEntities().map(e => e.id);
        forkJoin(ids.map(id => this.docService.cancelPreparation(id))).subscribe(() =>
          this.datasource.get(),
        );
      });
  }
}
